import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, TextField, useMediaQuery, ToggleButton, ToggleButtonGroup, MenuItem, Checkbox } from '@mui/material';
import theme from '../../util/theme/theme';
import BottomSheetDialog from './mobile/mobile-bottom-sheet';
import { TierConfig, INCLUSIONS, AddonConfig, AddonKey, addonLabel, isInclusionKey, parseCapacityKey, STORAGE_SIZES_MB, DEFAULT_STORAGE_MB, formatStorage, formatHorizon, CapacityKind } from '../../util/subscription-tiers';

type EditSubscriptionTierModalProps = {
    open: boolean;
    tier: TierConfig | null;
    // The add-on catalog (Admin > Subscription Add-ons).
    addons?: AddonConfig[];
    onClose: () => void;
    // newAddonPrices: a price typed for an add-on that wasn't offered yet -
    // saving it offers the add-on at that price (shared by every plan).
    onSave: (updated: TierConfig, newAddonPrices: Record<string, number>) => void;
};

// Parent rows for capacity add-ons in Features & add-ons. Horizon add-ons get a
// fixed parent row of their own (no toggle, no price).
const HORIZON_PARENT = 'projectionHorizon';
const CHILD_KIND: Record<string, CapacityKind> = { userAcl: 'users', documents: 'storage', [HORIZON_PARENT]: 'horizon' };

const EditSubscriptionTierModal: React.FC<EditSubscriptionTierModalProps> = ({ open, tier, addons = [], onClose, onSave }) => {
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [form, setForm] = useState<TierConfig | null>(null);
    const [newPrices, setNewPrices] = useState<Record<string, string>>({});

    useEffect(() => {
        setForm(tier);
        setNewPrices({});
    }, [tier, open]);

    if (!form) return null;

    const handleSave = () => {
        // Only for add-ons this tier now sells.
        const listed = form.availableAddons || [];
        const prices = Object.fromEntries(Object.entries(newPrices)
            .filter(([key, value]) => value !== '' && listed.includes(key as AddonKey) && Number(value) >= 0)
            .map(([key, value]) => [key, Number(value)]));
        onSave(form, prices);
    };

    const content = (
        <>
            <DialogTitle variant="h6" fontWeight={600}>
                Edit {form.name} {form.billing ? `(${form.billing === 'annual' ? '12 Months Up Front' : 'No Commitment'})` : ''}
            </DialogTitle>
            <DialogContent>
                <Box display="flex" flexDirection="column" gap={3} mt={1}>
                    {/* Name and card position are shared by the plan's Monthly
                        and Yearly versions - saving one updates both. */}
                    <Box display="flex" gap={2}>
                        <Box flex={2}>
                            <Typography mb={1} fontSize={14} color="var(--color-text-muted)">Plan name</Typography>
                            <TextField
                                value={form.name}
                                onChange={e => setForm({ ...form, name: e.target.value })}
                                size="small"
                                fullWidth
                                disabled={form.id === 'free'}
                            />
                        </Box>
                        <Box flex={1}>
                            <Typography mb={1} fontSize={14} color="var(--color-text-muted)">Display order</Typography>
                            <TextField
                                type="number"
                                value={form.sortOrder ?? 0}
                                onChange={e => setForm({ ...form, sortOrder: Math.max(0, parseInt(e.target.value, 10) || 0) })}
                                size="small"
                                fullWidth
                                helperText="Lowest shows first"
                            />
                        </Box>
                    </Box>
                    <Box>
                        <Typography mb={1} fontSize={14} color="var(--color-text-muted)">Billing note</Typography>
                        <TextField
                            value={form.billingNote}
                            onChange={e => setForm({ ...form, billingNote: e.target.value })}
                            size="small"
                            fullWidth
                        />
                    </Box>
                    <Box display="flex" gap={2}>
                        <Box flex={1}>
                            <Typography mb={1} fontSize={14} color="var(--color-text-muted)">Price per month ($)</Typography>
                            <TextField
                                type="number"
                                value={form.pricePerMonth}
                                onChange={e => setForm({ ...form, pricePerMonth: Number(e.target.value) || 0 })}
                                size="small"
                                fullWidth
                            />
                        </Box>
                        <Box flex={1}>
                            <Typography mb={1} fontSize={14} color="var(--color-text-muted)">
                                Amount charged now ($)
                            </Typography>
                            <TextField
                                type="number"
                                value={form.minimumGuaranteed}
                                onChange={e => setForm({ ...form, minimumGuaranteed: Number(e.target.value) || 0 })}
                                size="small"
                                fullWidth
                                helperText={form.billing === 'annual' ? 'The full 12-month commitment' : "One month's price"}
                            />
                        </Box>
                    </Box>
                    <Box display="flex" gap={2}>
                        <Box flex={1}>
                            <Typography mb={1} fontSize={14} color="var(--color-text-muted)">Cash flow horizon (days)</Typography>
                            <TextField
                                type="number"
                                value={form.horizonDays}
                                onChange={e => setForm({ ...form, horizonDays: Number(e.target.value) || 0 })}
                                size="small"
                                fullWidth
                            />
                        </Box>
                        <Box flex={1}>
                            <Typography mb={1} fontSize={14} color="var(--color-text-muted)">Users</Typography>
                            <TextField
                                type="number"
                                value={form.users}
                                onChange={e => setForm({ ...form, users: Number(e.target.value) || 0 })}
                                size="small"
                                fullWidth
                            />
                        </Box>
                    </Box>
                    {/* Documents storage - fixed sizes; the middle one until
                        picked. Only matters on a plan with Documents. */}
                    <Box>
                        <Typography mb={1} fontSize={14} color="var(--color-text-muted)">Documents storage</Typography>
                        <TextField
                            select
                            value={form.storageMb ?? DEFAULT_STORAGE_MB}
                            onChange={e => setForm({ ...form, storageMb: Number(e.target.value) })}
                            size="small"
                            fullWidth
                        >
                            {STORAGE_SIZES_MB.map(mb => <MenuItem key={mb} value={mb}>{formatStorage(mb)}</MenuItem>)}
                        </TextField>
                    </Box>
                    {/* What the plan includes and which add-ons companies on
                        it can buy, in one list (express-server
                        util/subscription-inclusions.js). A feature is
                        Included, sold as an Add-on, or Off; a capacity add-on
                        (more users / a longer horizon) is an Add-on or Off.
                        Add-on prices are set in Admin > Subscription Add-ons. */}
                    <Box>
                        <Typography mb={1} fontSize={14} color="var(--color-text-muted)">Features &amp; add-ons</Typography>
                        <Box sx={{ border: '1px solid rgba(0,0,0,0.12)', borderRadius: 2, overflow: 'hidden' }}>
                            {[...INCLUSIONS.map(i => i.key as string), HORIZON_PARENT].map((key, index) => {
                                const per = form.billing === 'annual' ? 'year' : 'month';
                                // Free has no billing - nothing can be sold on it.
                                const canSell = !!form.billing;
                                // Capacity add-ons sit as checkboxes under their parent row:
                                // user packs under User access control, storage under
                                // Documents, horizon under the fixed Projection horizon row.
                                const childKind = CHILD_KIND[key];
                                const childAddons = (show: boolean) => (show && childKind
                                    ? addons.filter(a => parseCapacityKey(a.addonKey)?.kind === childKind)
                                    : []);
                                const toggleChild = (childKey: AddonKey, on: boolean) => {
                                    const list = (form.availableAddons || []).filter(k => k !== childKey);
                                    setForm({ ...form, availableAddons: on ? [...list, childKey] : list });
                                };
                                const renderChildren = (children: AddonConfig[]) => children.map(child => {
                                    const childKey = child.addonKey as AddonKey;
                                    const capacity = parseCapacityKey(childKey);
                                    const amountLabel = capacity?.kind === 'storage' ? formatStorage(capacity.amount)
                                        : capacity?.kind === 'horizon' ? formatHorizon(capacity.amount)
                                        : `${capacity?.amount} user${capacity?.amount === 1 ? '' : 's'}`;
                                    const childPrice = form.billing === 'annual' ? child.pricePerYear : child.pricePerMonth;
                                    const sellable = canSell && child.isOffered;
                                    return (
                                        <Box key={childKey} sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 4, pr: 1.5, py: 0.25, borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                                            <Checkbox
                                                size="small"
                                                checked={(form.availableAddons || []).includes(childKey)}
                                                disabled={!sellable}
                                                onChange={e => toggleChild(childKey, e.target.checked)}
                                            />
                                            <Typography fontSize={13.5} flex={1} color={sellable ? 'inherit' : 'var(--color-text-muted)'}>+ {amountLabel}</Typography>
                                            <Typography fontSize={12} color="var(--color-text-muted)">
                                                {child.isOffered ? `$${Number(childPrice).toLocaleString()} / ${per}` : 'Not offered'}
                                            </Typography>
                                        </Box>
                                    );
                                });

                                // The fixed parent for horizon add-ons - no toggle, no price.
                                if (key === HORIZON_PARENT) {
                                    return (
                                        <React.Fragment key={key}>
                                            <Box sx={{ px: 1.5, py: 1, borderTop: index ? '1px solid rgba(0,0,0,0.08)' : 'none' }}>
                                                <Typography fontSize={14}>Projection horizon</Typography>
                                            </Box>
                                            {renderChildren(childAddons(true))}
                                        </React.Fragment>
                                    );
                                }

                                const featureKey = key as AddonKey;
                                const addon = addons.find(a => a.addonKey === featureKey);
                                const listed = (form.availableAddons || []).includes(featureKey);
                                const mode: 'included' | 'addon' | 'off' = isInclusionKey(featureKey) && form[featureKey] ? 'included' : listed ? 'addon' : 'off';
                                const setMode = (next: 'included' | 'addon' | 'off') => {
                                    // Turned off - its child add-ons go with it.
                                    const list = (form.availableAddons || []).filter(k => k !== featureKey && !(next === 'off' && childKind && parseCapacityKey(k)?.kind === childKind));
                                    setForm({
                                        ...form,
                                        [featureKey]: next === 'included',
                                        availableAddons: next === 'addon' ? [...list, featureKey] : list,
                                    });
                                };
                                const price = addon ? (form.billing === 'annual' ? addon.pricePerYear : addon.pricePerMonth) : null;
                                // A feature's add-on price is set right here (shared by every plan).
                                const needsPrice = canSell && mode !== 'included';
                                const typedPrice = newPrices[featureKey] ?? (mode === 'addon' && addon?.isOffered ? String(price ?? '') : '');
                                const setTypedPrice = (value: string) => {
                                    setNewPrices(prev => ({ ...prev, [featureKey]: value }));
                                    const list = (form.availableAddons || []).filter(k => k !== featureKey);
                                    setForm({
                                        ...form,
                                        ...(value !== '' ? { [featureKey]: false } : {}),
                                        availableAddons: value !== '' ? [...list, featureKey] : list,
                                    });
                                };
                                return (
                                    <React.Fragment key={key}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 0.75, borderTop: index ? '1px solid rgba(0,0,0,0.08)' : 'none' }}>
                                        <Box flex={1} minWidth={0}>
                                            <Typography fontSize={14}>{addonLabel(featureKey)}</Typography>
                                        </Box>
                                        {needsPrice && (
                                            <TextField
                                                type="number"
                                                size="small"
                                                value={typedPrice}
                                                onChange={e => setTypedPrice(e.target.value)}
                                                placeholder={`$ / ${per === 'year' ? 'yr' : 'mo'}`}
                                                inputProps={{ min: 0, 'aria-label': `${addonLabel(featureKey)} add-on price per ${per}` }}
                                                sx={{ width: 96, '& .MuiInputBase-input': { py: 0.5, fontSize: 13 }, ...(typedPrice !== '' ? { '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--color-primary)' } } : {}) }}
                                            />
                                        )}
                                        <ToggleButtonGroup
                                            exclusive
                                            size="small"
                                            value={mode}
                                            onChange={(_, value) => {
                                                if (!value) return;
                                                if (needsPrice) setNewPrices(prev => ({ ...prev, [featureKey]: '' }));
                                                setMode(value);
                                            }}
                                            sx={{
                                                '& .MuiToggleButton-root': { textTransform: 'none', fontSize: 12, py: 0.25, px: 1.25 },
                                                '& .MuiToggleButton-root.Mui-selected, & .MuiToggleButton-root.Mui-selected:hover': { background: 'var(--color-primary)', color: '#fff' },
                                            }}
                                        >
                                            <ToggleButton value="included">Included</ToggleButton>
                                            {!needsPrice && <ToggleButton value="addon" disabled={!canSell}>Add-on</ToggleButton>}
                                            <ToggleButton value="off">Off</ToggleButton>
                                        </ToggleButtonGroup>
                                    </Box>
                                    {renderChildren(childAddons(mode !== 'off'))}
                                    </React.Fragment>
                                );
                            })}
                        </Box>
                        {!form.billing && (
                            <Typography fontSize={12} color="var(--color-text-muted)" mt={0.75}>
                                The Free plan has no billing, so it can't sell add-ons.
                            </Typography>
                        )}
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
                <Button
                    onClick={onClose}
                    variant="contained"
                    sx={{ background: 'var(--color-container-background)', color: 'var(--color-default-text)', borderRadius: 2, fontWeight: 600, boxShadow: 'none', textTransform: 'none' }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    sx={{ background: 'var(--color-primary)', color: '#fff', borderRadius: 2, fontWeight: 600, boxShadow: 'none', textTransform: 'none' }}
                >
                    Save Changes
                </Button>
            </DialogActions>
        </>
    );

    return isMobile ? (
        <BottomSheetDialog open={open} onClose={onClose} heightVh={90}>
            {content}
        </BottomSheetDialog>
    ) : (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth sx={{ '& .MuiPaper-root': { borderRadius: 3 } }}>
            {content}
        </Dialog>
    );
};

export default EditSubscriptionTierModal;
